/**
 * Global toast (Sonner-style shadcn Toaster). Policy for Office Rhythm:
 *
 * - **Prefer `variant: "destructive"`** (or the default) for *failures* and
 *   blocking issues: auth/session, save errors, network/RPC errors.
 * - **Do not** fire success toasts for outcomes that are *already obvious*
 *   in the UI the user is looking at (e.g. Notification Centre list updates,
 *   modals that close, calendar tiles that refetch). Keeps a single, calm
 *   place for in-flow feedback (e.g. the bell panel) instead of a corner
 *   pop-up duplicating the same information.
 * - **Success toasts** are still OK when the user needs explicit confirmation
 *   after a long or risky action, or when there is *no* obvious surface to
 *   show it (e.g. background sync that completed while they were elsewhere).
 * - The **bell** is for *events* (invites, reminders, actionable notices) —
 *   not for routine “saved” feedback on settings/profile. Use inline or
 *   in-view confirmation (or silence) there instead of `notifications` rows.
 */
import * as React from "react";

import type { ToastActionElement, ToastProps } from "@/components/ui/toast";

const TOAST_LIMIT        = 1;
const TOAST_REMOVE_DELAY = 4000; // 4 seconds — prevents module-level memory leak

type ToasterToast = ToastProps & {
  id:           string;
  title?:       React.ReactNode;
  description?: React.ReactNode;
  action?:      ToastActionElement;
};

// Note: shadcn's scaffold ships this as a `const ... as const` plus
// `type ActionType = typeof actionTypes`. We only ever use the type, so
// we declare it directly — dead runtime objects are bundler-unfriendly.
type ActionType = {
  ADD_TOAST:     "ADD_TOAST";
  UPDATE_TOAST:  "UPDATE_TOAST";
  DISMISS_TOAST: "DISMISS_TOAST";
  REMOVE_TOAST:  "REMOVE_TOAST";
};

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

type Action =
  | { type: ActionType["ADD_TOAST"];     toast: ToasterToast }
  | { type: ActionType["UPDATE_TOAST"];  toast: Partial<ToasterToast> }
  | { type: ActionType["DISMISS_TOAST"]; toastId?: ToasterToast["id"] }
  | { type: ActionType["REMOVE_TOAST"];  toastId?: ToasterToast["id"] };

interface State {
  toasts: ToasterToast[];
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) return;

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({ type: "REMOVE_TOAST", toastId });
  }, TOAST_REMOVE_DELAY);

  toastTimeouts.set(toastId, timeout);
};

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      };

    case "DISMISS_TOAST": {
      const { toastId } = action;
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast) => addToRemoveQueue(toast.id));
      }
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined ? { ...t, open: false } : t
        ),
      };
    }

    case "REMOVE_TOAST":
      if (action.toastId === undefined) return { ...state, toasts: [] };
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
  }
};

const listeners: Array<(state: State) => void> = [];
let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => listener(memoryState));
}

type Toast = Omit<ToasterToast, "id">;

function toast({ ...props }: Toast) {
  const id = genId();

  const update  = (props: ToasterToast) => dispatch({ type: "UPDATE_TOAST", toast: { ...props, id } });
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id });

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => { if (!open) dismiss(); },
    },
  });

  return { id, dismiss, update };
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) listeners.splice(index, 1);
    };
  }, [state]);

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
  };
}

export { useToast, toast };
